import { Button } from 'tio-ui/components/buttons';
import { Form, type FormResultData } from 'tio-ui/components/forms';
import * as v from 'valibot';
import { t } from 'ember-intl';
import { Section, VStack } from 'tio-ui/components/layout';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import inputmask from 'tio-common/modifiers/inputmask';
import type Owner from '@ember/owner';
import { divide } from 'tio-ui/math';

const formSchema = v.object({
  reportedSalary: v.pipe(v.string(), v.nonEmpty('Please enter a salary')),
  email: v.pipe(
    v.string(),
    v.trim(),
    v.nonEmpty('Please enter an email'),
    v.email('Please enter a valid email address')
  ),
});

type FormData = v.InferOutput<typeof formSchema>;

interface SyfPostEnrollmentSignature {
  Args: {
    reportedSalary: number;
    emailAddress: string;
    reportedSalaryFieldLabel: string;
    loading: boolean;
    onSubmit: (emailAddress: string, reportedSalary: string) => void;
    onSkip: () => void;
  };
}

class SyfEnrollmentConfirmationComponent extends Component<SyfPostEnrollmentSignature> {
  @tracked formData: FormData;

  constructor(owner: Owner, args: SyfPostEnrollmentSignature['Args']) {
    super(owner, args);
    this.formData = {
      email: args.emailAddress || '',
      reportedSalary: args.reportedSalary ? String(divide(args.reportedSalary, 100)) : '',
    };
  }

  handleFormChange = (result: FormResultData<FormData>) => {
    this.formData = result.data;
  };

  handleFormSubmit = (result: FormResultData<FormData>) => {
    const { email, reportedSalary } = result.data;
    this.args.onSubmit(email, String(reportedSalary));
  };

  <template>
    <Section>
      <:header>
        {{t "syf.post_enrollment.header"}}
      </:header>
      <:body>
        <Form
          @data={{this.formData}}
          @schema={{formSchema}}
          @onChange={{this.handleFormChange}}
          @onSubmit={{this.handleFormSubmit}}
          class="md:w-1/2"
          as |form|
        >
          <VStack>
            <form.Field @name="reportedSalary" as |field|>
              <field.Input
                @label={{@reportedSalaryFieldLabel}}
                @description={{t "syf.post_enrollment.description"}}
                @isRequired={{true}}
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
            </form.Field>
            <form.Field @name="email" as |field|>
              <field.Input
                @type="email"
                @label={{t "syf.post_enrollment.personal_email_label"}}
                @description={{t "syf.post_enrollment.personal_email_description"}}
                @isRequired={{true}}
              />
            </form.Field>

            <div class="flex justify-center">
              <Button
                class="m-auto px-8 mb-4"
                type="submit"
                @intent="primary"
                @isRunning={{@loading}}
              >
                {{t "syf.post_enrollment.continue_button"}}
              </Button>
            </div>
          </VStack>
        </Form>
      </:body>
    </Section>
  </template>
}

export default SyfEnrollmentConfirmationComponent;
